import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import Dropdown from './Dropdown';

/**
 * Delete Reason Modal
 *
 * Generic modal for capturing deletion reasons with audit trail.
 * Used across all modules for compliance and accountability.
 *
 * @param {boolean} isOpen - Modal visibility
 * @param {function} onClose - Close handler (cancel)
 * @param {function} onConfirm - Confirm handler with reason data
 * @param {object} item - Item being deleted { id, name, type, details... }
 * @param {string} module - Module name (equipment, crew, defects, etc.)
 * @param {array} reasons - Array of reason objects
 * @param {string} deleteType - 'soft' or 'hard' delete
 * @param {boolean} allowHardDelete - Show hard delete option (Fleet only)
 */
const DeleteReasonModal = ({
  isOpen,
  onClose,
  onConfirm,
  item = {},
  module = 'equipment',
  reasons = [],
  deleteType: initialDeleteType = 'soft',
  allowHardDelete = false
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');
  const [authorizedBy, setAuthorizedBy] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [deleteType, setDeleteType] = useState(initialDeleteType);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setNotes('');
      setAuthorizedBy('');
      setConfirmed(false);
      setDeleteType(initialDeleteType);
    }
  }, [isOpen, initialDeleteType]);

  if (!isOpen) return null;

  // Find selected reason object
  const selectedReasonObj = reasons.find(r => r.code === selectedReason);
  const notesRequired = selectedReasonObj?.requiresNotes || false;

  // Validation
  const canSubmit = selectedReason &&
                    confirmed &&
                    (!notesRequired || (notesRequired && notes.trim()));

  const handleConfirm = () => {
    if (!canSubmit) return;

    onConfirm({
      reason: {
        code: selectedReason,
        label: selectedReasonObj?.label || selectedReason,
        notes: notes.trim()
      },
      authorizedBy: authorizedBy.trim() || null,
      deleteType
    });
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-red-50">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Confirm Deletion
                </h3>
                <p className="mt-0.5 text-sm text-gray-600">
                  This action requires documentation
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Item Details */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="text-sm">
              <p className="font-medium text-gray-900">Item to be deleted:</p>
              <p className="mt-1 text-gray-700 font-semibold">
                {item.name || item.label || item.type || 'Unknown item'}
              </p>
              {item.category && (
                <p className="mt-0.5 text-gray-600 text-xs">
                  {item.category} • {item.location || 'Unknown location'}
                </p>
              )}
              {item.serialNumber && (
                <p className="mt-0.5 text-gray-600 text-xs font-mono">
                  Serial: {item.serialNumber}
                </p>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="p-6 space-y-4">
            {/* Delete Type (if allowed) */}
            {allowHardDelete && (
              <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Deletion Type
                </label>
                <div className="space-y-2">
                  <label className="flex items-start">
                    <input
                      type="radio"
                      value="soft"
                      checked={deleteType === 'soft'}
                      onChange={(e) => setDeleteType(e.target.value)}
                      className="mt-0.5 h-4 w-4 text-cyan-600 border-gray-300 focus:ring-cyan-500"
                    />
                    <span className="ml-2 text-sm">
                      <span className="font-medium text-gray-900">Soft delete (recommended)</span>
                      <span className="block text-gray-600">Can be restored within 30 days</span>
                    </span>
                  </label>
                  <label className="flex items-start">
                    <input
                      type="radio"
                      value="hard"
                      checked={deleteType === 'hard'}
                      onChange={(e) => setDeleteType(e.target.value)}
                      className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500"
                    />
                    <span className="ml-2 text-sm">
                      <span className="font-medium text-gray-900">Permanent delete</span>
                      <span className="block text-red-600">Cannot be recovered</span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Reason Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Reason for deletion <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={selectedReason}
                onChange={setSelectedReason}
                width="w-full"
                placeholder="Select a reason..."
                options={reasons.map(reason => ({
                  value: reason.code,
                  label: reason.label
                }))}
              />
              {selectedReasonObj?.description && (
                <p className="mt-1 text-xs text-gray-500">
                  {selectedReasonObj.description}
                </p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-900 mb-2">
                Additional notes {notesRequired && <span className="text-red-500">*</span>}
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder={notesRequired ? "Please provide details..." : "Optional: Add any relevant details..."}
                className="block w-full rounded-md px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm resize-none"
              />
            </div>

            {/* Authorized By */}
            <div>
              <label htmlFor="authorizedBy" className="block text-sm font-medium text-gray-900 mb-2">
                Authorized by (optional)
              </label>
              <input
                id="authorizedBy"
                type="text"
                value={authorizedBy}
                onChange={(e) => setAuthorizedBy(e.target.value)}
                placeholder="e.g., Captain John Smith"
                className="block w-full rounded-md px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                If someone else authorized this deletion, enter their name
              </p>
            </div>

            {/* Confirmation Checkbox */}
            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  I confirm this deletion is accurate and necessary
                </span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <div className="text-xs text-gray-600">
              <AlertTriangle className="inline h-3 w-3 mr-1" />
              This action will be logged in the audit trail
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!canSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {deleteType === 'hard' ? 'Delete Permanently' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeleteReasonModal;
